import { useMemo } from "react";
import type { CSSProperties } from "react";
import { useSelector } from "react-redux";
import { Button, DatePicker, Input, InputNumber, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { buildAuthorOptions } from "@mappers/notes.mapper.mjs";
import type { NoteFilterState } from "@mappers/notes.mapper.mjs";
import { DATE_FORMAT } from "@utils/date_utils";
import { formattedPlayerName } from "@utils/name_utils";
import type { NotesState } from "@store/reducers/notes.reducer";

export interface NotesRange {
    from: string;
    to: string;
}

interface TeamPlayer {
    player_id: number;
    first_name: string;
    last_name: string;
}

interface NotesFiltersProps {
    filters: NoteFilterState;
    onChange: (patch: Partial<NoteFilterState>) => void;
    range: NotesRange;
    onRangeChange: (range: NotesRange) => void;
    onClear: () => void;
    /** Set on a Player page. The player filter shows this player and is locked. */
    fixedPlayerId?: number;
}

export const NotesFilters = ({ filters, onChange, range, onRangeChange, onClear, fixedPlayerId }: NotesFiltersProps) => {
    const players: TeamPlayer[] = useSelector(({ team }: any) => team.players);
    const teamName: string = useSelector(({ team }: any) => team.info.display_name);
    const myUuid: string | undefined = useSelector(({ auth }: any) => auth.currentUser.uuid);
    const notes = useSelector((state: { notes: NotesState }) => state.notes.notes);
    const tags = useSelector((state: { notes: NotesState }) => state.notes.tags);
    const attributes = useSelector((state: { notes: NotesState }) => state.notes.attributes);

    const playerLabel = (player: TeamPlayer) => formattedPlayerName(teamName, `${player.first_name} ${player.last_name}`);
    const playerOptions = useMemo(() => players.map((p) => ({ value: p.player_id, label: playerLabel(p) })), [players, teamName]);
    // A tag on no note the user can see would filter out every note, so the filter leaves it out.
    const tagOptions = useMemo(
        () => tags.filter((t) => t.count > 0).map((t) => ({ value: t.name, label: `${t.name} (${t.count})` })),
        [tags],
    );
    const authorOptions = useMemo(() => buildAuthorOptions(notes, myUuid), [notes, myUuid]);
    const attributeOptions = useMemo(() => attributes.map((a) => ({ value: a.id, label: a.name })), [attributes]);

    const chosenAttribute = attributes.find((a) => a.id === filters.attributeId);

    return (
        <div className="notes-filters" style={{ "--notes-filter-count": 4 } as CSSProperties}>
            {/* The date range loads notes from the server. The other filters narrow the loaded notes. */}
            <DatePicker.RangePicker
                aria-label="Date range"
                allowClear={false}
                format={DATE_FORMAT}
                value={[dayjs(range.from), dayjs(range.to)]}
                onChange={(dates) => {
                    if (dates?.[0] && dates[1])
                        onRangeChange({
                            from: dates[0].format(DATE_FORMAT),
                            to: dates[1].format(DATE_FORMAT),
                        });
                }}
            />
            <Input
                aria-label="Search note text"
                className="notes-filters-search"
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search note text…"
                value={filters.text}
                onChange={(e) => onChange({ text: e.target.value })}
            />
            <Button type="link" className="notes-filters-clear" onClick={onClear} data-attr="notes.filters.clear">
                Clear all
            </Button>
            <Select
                aria-label="Players"
                className="notes-full-width"
                mode="multiple"
                allowClear={fixedPlayerId === undefined}
                disabled={fixedPlayerId !== undefined}
                maxTagCount="responsive"
                optionFilterProp="label"
                placeholder="Any player"
                options={playerOptions}
                value={fixedPlayerId === undefined ? filters.players : [fixedPlayerId]}
                onChange={(value) => onChange({ players: value })}
            />
            <div>
                <Select
                    aria-label="Tags"
                    className="notes-full-width"
                    mode="multiple"
                    allowClear
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    optionLabelProp="value"
                    placeholder="Any tag"
                    options={tagOptions}
                    value={filters.tags}
                    onChange={(value) => onChange({ tags: value })}
                />
            </div>
            <div>
                <Select
                    aria-label="Author"
                    className="notes-full-width"
                    mode="multiple"
                    allowClear
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    placeholder="Any author"
                    options={authorOptions}
                    value={filters.authorUuids}
                    onChange={(value) => onChange({ authorUuids: value })}
                />
            </div>
            <div>
                <Select
                    aria-label="Has attribute"
                    className="notes-full-width"
                    mode="multiple"
                    allowClear
                    maxTagCount="responsive"
                    optionFilterProp="label"
                    placeholder="Has attribute"
                    options={attributeOptions}
                    value={filters.attributeIds}
                    onChange={(value) => onChange({ attributeIds: value })}
                />
            </div>
            {/* Hidden for now: the single attribute filter.
                <div>
                    <Select
                        aria-label="Attribute"
                        className="notes-filter-attribute"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Any attribute"
                        options={attributeOptions}
                        value={filters.attributeId ?? undefined}
                        onChange={(value) => onChange({ attributeId: value ?? null, min: null, max: null })}
                    />
                </div>
                */}
            {/* Hidden for now: the min and max inputs of the attribute filter.
                {chosenAttribute && (
                    <div className="notes-filter-range">
                        <InputNumber
                            aria-label="Minimum value"
                            className="notes-filter-number"
                            placeholder={chosenAttribute.min_value == null ? "Min" : `Min ${chosenAttribute.min_value}`}
                            value={filters.min}
                            onChange={(value) => onChange({ min: value })}
                        />
                        <span>–</span>
                        <InputNumber
                            aria-label="Maximum value"
                            className="notes-filter-number"
                            placeholder={chosenAttribute.max_value == null ? "Max" : `Max ${chosenAttribute.max_value}`}
                            value={filters.max}
                            onChange={(value) => onChange({ max: value })}
                        />
                    </div>
                )}
                */}
        </div>
    );
};
